// pages/manager/ManagerTaskTable.jsx
//
// The Manager "Ticket Assignment & Progress" table, shared by Manager Home
// (ManagerDashboard.js) and Manager -> Team Complaints
// (ManagerComplaintsPage.jsx) so both render the SAME rows, chips, filters
// and actions. Also holds the pure helpers that table needs (moved here from
// ManagerDashboard.js unchanged -- the dashboard imports them back).
//
// Complaint scope: a row is a team complaint when its task type is
// IT_SUPPORT. GET /tasks already reports a legacy NULL task type as
// IT_SUPPORT (COALESCE in taskController.getTasks), and GET /tasks
// ?complaintsOnly=1 filters server-side by the same rule; isTeamComplaint()
// is the matching client-side rule. Both pages show every work flow
// (utils/workGroup.js) in work-flow tabs; a complaint row is assigned to a
// Technician only, other rows within their own work group.
//
// The component is fully controlled: the page owns search / status /
// member / quick-filter / page / rows-per-page / drafts, and passes the
// save/open handlers. Unit security stays server-side (GET /tasks and
// GET /manager/team-users are scoped by req.user.unit_id); nothing here
// widens what the page already loaded.
import React, { useEffect, useMemo } from "react";
import { useMediaQuery } from "@mui/material";
import { FaListCheck, FaFilter, FaBoxOpen, FaUserGear } from "react-icons/fa6";
import { compactPageItems } from "../../components/ui/compactPagination";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import { isComplaintTask, complaintAssigneeOptions, roleAssigneeOptions } from "../../utils/complaintAssignee";
import { workGroupOf, GROUP_ROLE, GROUP_ASSIGN_NOUN } from "../../utils/workGroup";
import "./ManagerTaskTable.css";

/* ══════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════ */
export const STATUS_LABELS = {
  pending:"Pending", assigned:"Assigned", open:"Open",
  in_progress:"In Progress", completed:"Completed",
  closed:"Closed", cancelled:"Cancelled", rejected:"Rejected", resolved:"Resolved",
};

export const STATUS_PROGRESS = {
  pending:10, open:20, assigned:30, in_progress:60,
  resolved:90, completed:100, closed:100, cancelled:0, rejected:0,
};

/* ══════════════════════════════════════════
   PURE HELPERS
══════════════════════════════════════════ */
export function getText(v,fb="-"){
  if(v===null||v===undefined||v==="")return fb;
  if(typeof v==="string"||typeof v==="number"||typeof v==="boolean")return String(v);
  if(Array.isArray(v)){const t=v.map(i=>getText(i,"")).filter(Boolean).join(", ");return t||fb;}
  if(typeof v==="object")return v.name||v.title||v.label||v.username||v.email||v.value||fb;
  return fb;
}
export const normalizeStatus = v => String(getText(v,"pending")).trim().toLowerCase().replace(/\s+/g,"_");
export const titleCase = v => String(getText(v,"-")).replace(/_/g," ").replace(/\w\S*/g,t=>t.charAt(0).toUpperCase()+t.slice(1));
export const safeNumber = v => { const n=Number(v); return Number.isFinite(n)?n:0; };
export const getIdValue  = v => { if(v===null||v===undefined||v==="")return ""; if(typeof v==="object")return v.id||v.value||""; return v; };

export const getTaskProgress = task => {
  const e = task?.progress??task?.progress_percent??task?.completion_percent??task?.percent_completed??task?.completed_percent;
  if(e!==undefined&&e!==null&&e!=="")return Math.max(0,Math.min(100,safeNumber(e)));
  return STATUS_PROGRESS[normalizeStatus(task?.status)]??0;
};

export const getProgressText = task => {
  const p=getTaskProgress(task); const s=normalizeStatus(task?.status);
  if(p===100)return "Completed";
  if(["cancelled","rejected"].includes(s))return "Stopped";
  if(p>=60)return "Work in progress";
  if(p>=30)return "Assigned to technician";
  if(p>0)return "Waiting to start";
  return "Not started";
};

export const formatShortDate = v => {
  if(!v)return "";
  const d=new Date(v);
  if(Number.isNaN(d.getTime()))return "";
  return d.toLocaleString([],{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"});
};

export const getLastRemark = task => {
  const remarks=Array.isArray(task?.remarks)?task.remarks:[];
  if(remarks.length){
    const sorted=[...remarks].filter(r=>r?.remark).sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
    const l=sorted[0];
    if(l)return{text:getText(l.remark,""),type:getText(l.remark_type,"general"),time:l.created_at,by:getText(l.created_by?.name,"")||getText(l.created_by_name,"")||getText(l.created_by,"")};
  }
  const candidates=[
    {text:task?.last_remark,type:task?.last_remark_type||"latest",time:task?.last_remark_at,by:task?.last_remark_by_name},
    {text:task?.remark_3,type:"R3",time:task?.remark_3_at},
    {text:task?.remark_2,type:"R2",time:task?.remark_2_at},
    {text:task?.remark_1,type:"R1",time:task?.remark_1_at},
  ].filter(c=>c.text);
  return candidates[0]||null;
};

/* ── statusClass → css class name ── */
export const statusCls = s => {
  const v=normalizeStatus(s);
  if(["completed","closed","resolved"].includes(v))return "completed";
  if(["cancelled","rejected"].includes(v))return "cancelled";
  if(["in_progress","open"].includes(v))return "progress";
  if(v==="assigned")return "assigned";
  if(v==="pending")return "pending";
  return "default";
};

/* ── Spin ── */
export const Spin = ({white,lg}) => <span className={`md-spin${white?" white":""}${lg?" lg":""}`}/>;

export const isDoneStatus = s => ["completed","closed","resolved"].includes(s);
export const isDeadStatus  = s => ["completed","closed","resolved","cancelled","rejected"].includes(s);
export const isOverdueTask = (t, now) => {
  if(!t?.due_at) return false;
  if(isDeadStatus(normalizeStatus(t.status))) return false;
  const d=new Date(t.due_at);
  return !Number.isNaN(d.getTime()) && d<now;
};

// Role text for a team-user row -- never guesses a role that isn't present
// in the row.
export const getMemberRole = u => {
  const r=[u?.role_name,u?.roleName,u?.roles?.name,u?.role].map(x=>getText(x,"")).find(Boolean);
  // "data-analyst" / "data_analyst" -> "Data Analyst"
  return r ? titleCase(r.replace(/[_-]+/g," ")) : "Member";
};

export const getAssigneeId = t => String(getIdValue(t?.assigned_to)||getIdValue(t?.assignedTo)||getIdValue(t?.assigned_to_id)||"");
export const getTaskCategoryId = t => String(getIdValue(t?.category_id)||getIdValue(t?.category)||"");

// Due/SLA badge over the task's own due_at ("Due Soon" = within 24h).
export const getDueBadge = (task, now) => {
  const s = normalizeStatus(task?.status);
  if(isDoneStatus(s)) return {label:"Completed", cls:"completed"};
  if(!task?.due_at) return {label:"No due date", cls:"muted"};
  const due=new Date(task.due_at);
  if(Number.isNaN(due.getTime())) return {label:"No due date", cls:"muted"};
  if(["cancelled","rejected"].includes(s)) return {label:"Stopped", cls:"muted"};
  if(due<now) return {label:"Overdue", cls:"overdue"};
  if((due-now)/3600000<=24) return {label:"Due Soon", cls:"duesoon"};
  return {label:"On Track", cls:"ontrack"};
};

// Priority badge -- same low/medium/high/critical tone convention as
// pages/tasks/StatusChips.jsx. Renders nothing for a task with no priority.
export const getPriorityInfo = priority => {
  const p=String(priority||"").toLowerCase();
  if(!p) return null;
  const cls = p==="critical" ? "critical" : p==="high" ? "high" : p==="low" ? "low" : "medium";
  return {label:titleCase(p), cls};
};

/* ══════════════════════════════════════════
   TEAM COMPLAINTS: scope, chips, filtering
══════════════════════════════════════════ */

// IT_SUPPORT by task_type_code. A row with no code falls back to its type
// name; no type at all = legacy IT Support (the backend's own convention).
// Same complaint rule every assignee dropdown uses (utils/complaintAssignee.js).
export const isTeamComplaint = isComplaintTask;

export const QUICK_FILTER_CHIPS = [
  ["all","All"],
  ["active","Active"],
  ["pending","Pending"],
  ["in_progress","In Progress"],
  ["overdue","Overdue"],
  ["missing_category","Missing Category"],
  ["completed","Completed"],
  ["needs_action","Needs Action"],
];

export const passesQuickFilter = (t, s, qf, now = new Date()) => {
  if(qf==="all") return true;
  if(qf==="active") return !isDeadStatus(s);
  if(qf==="pending") return s==="pending";
  if(qf==="in_progress") return ["in_progress","open"].includes(s);
  if(qf==="overdue") return isOverdueTask(t, now);
  if(qf==="missing_category") return !getTaskCategoryId(t) && !getText(t.category_name,"");
  if(qf==="completed") return isDoneStatus(s);
  if(qf==="needs_action") return !isDeadStatus(s); // unresolved: same as "active" today, kept as a distinct chip
  return true;
};

// Chip counts over the table's own scope (before search/status/member), so
// each number matches what selecting that chip alone would show.
export const computeChipCounts = (rows, now = new Date()) => {
  const counts = {all:rows.length, active:0, pending:0, in_progress:0, overdue:0, missing_category:0, completed:0, needs_action:0};
  rows.forEach(t=>{
    const s=normalizeStatus(t.status);
    if(!isDeadStatus(s)){ counts.active++; counts.needs_action++; }
    if(s==="pending") counts.pending++;
    if(["in_progress","open"].includes(s)) counts.in_progress++;
    if(isOverdueTask(t, now)) counts.overdue++;
    if(!getTaskCategoryId(t) && !getText(t.category_name,"")) counts.missing_category++;
    if(isDoneStatus(s)) counts.completed++;
  });
  return counts;
};

export const filterTaskRows = (rows, { search = "", status = "ALL", member = "ALL", quickFilter = "all" }, now = new Date()) => {
  const q=search.trim().toLowerCase();
  return rows.filter(t=>{
    const s=normalizeStatus(t.status);
    const lr=getLastRemark(t);
    const okS=status==="ALL"?true:s===status;
    const okT=member==="ALL"?true:getAssigneeId(t)===member;
    const okQ=passesQuickFilter(t, s, quickFilter, now);
    if(!q)return okS&&okT&&okQ;
    const hay=[getText(taskDisplayTitle(t),""),getText(t.description,""),getText(t.category_name,""),getText(t.sub_category_name,""),getText(t.assigned_to_name,""),getText(lr?.text,""),getText(lr?.by,""),s,titleCase(s)].filter(Boolean).join(" ").toLowerCase();
    return okS&&okT&&okQ&&hay.includes(q);
  });
};

// Status dropdown options from the loaded rows. Closed/Cancelled stay
// readable on rows but are not offered as a day-to-day filter.
export const buildStatusRows = rows => {
  const m=new Map(); rows.forEach(t=>{ const s=normalizeStatus(t.status); m.set(s,(m.get(s)||0)+1); });
  return [...m.entries()]
    .filter(([k])=>!["closed","cancelled","canceled"].includes(k))
    .map(([k,c])=>({key:k,label:STATUS_LABELS[k]||titleCase(k),count:c}));
};

// Assign/category draft per row, seeded from the task's current values.
export const buildInitialDrafts = rows => {
  const d={};
  rows.forEach(t=>{
    d[t.id]={
      assigned_to:getIdValue(t.assigned_to)||getIdValue(t.assignedTo)||getIdValue(t.assigned_to_id)||"",
      category_id:getIdValue(t.category_id)||getIdValue(t.categoryId)||getIdValue(t.category)||"",
      category_name:getText(t.category_name,"")||getText(t.category,"")||"",
    };
  });
  return d;
};

// Assign Tech options for one row, drawn ONLY from `teamMembers` -- the
// manager's own-unit roster from GET /manager/team-users (scoped by
// req.user.unit_id server-side). Same-role list only: the task's role is
// the first of the current owner's role, a role field on the task, a hint
// from its task type/project, or the creator's role; only when none is one
// of the three working roles are all three listed. Managers always listed.
//
// IT Support complaints are the exception: owned by a Technician only --
// the rule Raise Complaint auto-assignment and POST /tasks already apply,
// and PUT /tasks/assign/:id enforces for a Manager. Their list is the
// unit's Technicians, plus the CURRENT owner whatever their role (even if
// missing from the roster), so the saved value never silently disappears.
// The creator (created_by) is never added just for raising the ticket.
const ASSIGN_ROLES=["developer","technician","data analyst"];
const normRole=r=>String(r||"").toLowerCase().replace(/[_-]+/g," ").trim();
export const buildComplaintAssignOptions = (task, teamMembers) => complaintAssigneeOptions(teamMembers, task);
export const buildAssignOptions = (task, teamMembers) => {
  if(isTeamComplaint(task)) return buildComplaintAssignOptions(task, teamMembers);
  // Developer / Data Analyst (or Technician) WORK -- the server's work_group
  // (ticket flow + current owner, never the creator's role alone): that
  // role's same-unit members, plus the current owner kept as-is.
  const group=workGroupOf(task);
  if(group) return roleAssigneeOptions(teamMembers, task, GROUP_ROLE[group]);
  const ownerId=getAssigneeId(task);
  const ownerUser=ownerId?teamMembers.find(m=>String(getIdValue(m))===ownerId):null;
  const ownerRole=ownerUser?getMemberRole(ownerUser).toLowerCase():"";
  const creatorId=String(getIdValue(task.created_by)||getIdValue(task.created_by_id)||getIdValue(task.createdBy)||"");
  const creatorUser=creatorId?teamMembers.find(m=>String(getIdValue(m))===creatorId):null;
  const typeText=[task.task_type_code,task.task_type_name,task.task_type?.code,task.task_type?.name,typeof task.task_type==="string"?task.task_type:"",task.work_type]
    .map(x=>getText(x,"")).join(" ").toLowerCase();
  const typeRole=/support|technician|hardware|network/.test(typeText)?"technician"
    : /develop|coding|bug|feature/.test(typeText)?"developer"
    : /analy|report|data/.test(typeText)?"data analyst"
    : (task.project_id||task.project_name||task.project?.id)?"developer":"";
  const taskRoleKey=[
    ownerRole,
    task.assigned_to_role, task.assigned_role, task.assignee_role, task.owner_role, task.role_name, task.role,
    typeRole,
    task.created_by?.role_name, task.created_by?.role, task.created_by_role, task.creator_role,
    creatorUser?getMemberRole(creatorUser):"",
  ].map(x=>normRole(getText(x,""))).find(r=>ASSIGN_ROLES.includes(r));
  const allowedRoles=taskRoleKey?[taskRoleKey]:ASSIGN_ROLES;
  return teamMembers.filter(m=>{
    const r=normRole(getMemberRole(m));
    return r==="manager"||allowedRoles.includes(r);
  });
};

// "All Users" filter options: the manager's operational subordinates only --
// the logged-in manager and Manager/Admin/Staff-role records are dropped
// from the (already own-unit) /manager/team-users roster.
const EXCLUDED_TEAM_ROLES = ["manager","admin","administrator","staff"];
export const buildTeamFilterMembers = (teamMembers, selfUserId) => {
  const selfId = selfUserId!==undefined&&selfUserId!==null ? String(selfUserId) : null;
  return teamMembers.filter(m=>{
    const mid = String(getIdValue(m));
    if(selfId && mid===selfId) return false;
    return !EXCLUDED_TEAM_ROLES.includes(getMemberRole(m).toLowerCase());
  });
};

/* ══════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════ */
// `standardized` (opt-in, Team Complaints only): Ticket-Dashboard styling
// (ManagerTaskTable.css, scoped to .mtt-std) and, below 768px, one labelled
// card per row instead of a table row. Both layouts use the SAME cell
// renderers, drafts and handlers below -- only the wrapper markup differs.
// Manager Home leaves it off and keeps its original table look.
export default function ManagerTaskTable({
  tableRef,
  title = "Ticket Assignment & Progress",
  subtitle,
  scopeNote,
  rows,
  loading,
  now,
  search, onSearchChange,
  status, onStatusChange, statusOptions,
  member, onMemberChange, memberOptions,
  quickFilter, onQuickFilterChange,
  page, onPageChange,
  rowsPerPage, onRowsPerPageChange,
  onReset,
  drafts, onDraftChange,
  teamMembers,
  onOpen,
  onSave,
  savingId,
  standardized = false,
  // Team Complaints only: "Created By" = tasks.created_by (who raised the
  // ticket) -- not requested_for (who it is for) and not assigned_to (the
  // current owner, shown in Current Owner). Off by default, so Manager Home
  // keeps its layout.
  showCreatedBy = false,
  // Column header for the assignment dropdown. Both pages pass one per
  // work-flow tab ("Assign Developer" / "Assign Data Analyst" / "Assign
  // Technician"); each row's own work group still decides its options.
  assignLabel = "Assign Tech",
  // A filter the parent owns (the work-flow tab, Home's priority/category)
  // is active, so Reset is offered even when none of the table's own
  // filters is set.
  extraFiltersActive = false,
  // Extra parent-owned filter controls rendered in the toolbar (Home's
  // Priority / Category selects); the parent filters `rows` itself.
  toolbarExtra = null,
}) {
  const filtered = useMemo(
    ()=>filterTaskRows(rows, { search, status, member, quickFilter }, now),
    [rows, search, status, member, quickFilter, now]
  );
  const chipCounts = useMemo(()=>computeChipCounts(rows, now), [rows, now]);
  // Without matchMedia (tests, SSR) this is false, so the table renders.
  const isPhone = useMediaQuery("(max-width:767px)");
  const showCards = standardized && isPhone;

  useEffect(()=>{ onPageChange(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },[search,status,member,rowsPerPage,quickFilter]);

  const totalPages = Math.max(1,Math.ceil(filtered.length/rowsPerPage));
  const i0 = (page-1)*rowsPerPage;
  const pageData = filtered.slice(i0,i0+rowsPerPage);
  const hasFilters = extraFiltersActive||search||status!=="ALL"||member!=="ALL"||quickFilter!=="all";

  /* ── cell renderers, shared by the table rows and the phone cards ── */
  // Flow comes from the server (GET /tasks ticket_flow -- type + ownership,
  // services/ticketFlowService.js), never from the creator's role alone.
  const flowLabel = (task) => {
    const flow=getText(task.ticket_flow,"").toUpperCase();
    if(flow==="OWN_TICKET") return "Own Ticket";
    if(flow==="IT_SUPPORT_COMPLAINT") return "IT Support Complaint";
    return getText(task.task_type_name,"")||titleCase(getText(task.task_type_code,"").toLowerCase().replace(/_/g," "))||"";
  };

  const renderTask = (task) => {
    const name=getText(taskDisplayTitle(task));
    const desc=getText(task.description,"—");
    const flow=flowLabel(task);
    const requestedFor=getText(task.requested_for_name,"");
    const unit=getText(task.unit_name,"");
    return (
      <div className="md-task-cell">
        <div className="md-task-av" aria-hidden="true">{getText(taskDisplayTitle(task),"T").charAt(0).toUpperCase()}</div>
        <div className="md-task-text">
          <div className="md-task-name" title={name}>{name}</div>
          <div className="md-task-desc" title={desc}>{desc}</div>
          {(flow||requestedFor||unit) && (
            <div className="mt-task-meta">
              {flow && <span className="mt-flow-badge" data-flow={getText(task.ticket_flow,"")}>{flow}</span>}
              {requestedFor && <span className="mt-meta-item">For: {requestedFor}</span>}
              {unit && <span className="mt-meta-item">{unit}</span>}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPriority = (task) => {
    const priorityInfo=getPriorityInfo(task.priority);
    return priorityInfo ? <span className={`md-badge-priority ${priorityInfo.cls}`}>{priorityInfo.label}</span> : <span className="md-no-remark">—</span>;
  };

  const renderProgress = (task) => {
    const prog=getTaskProgress(task);
    return (
      <div className="md-prog-cell">
        <span className={`md-badge ${statusCls(task.status)}`}>{STATUS_LABELS[task.status]||titleCase(task.status)}</span>
        <div className="md-prog-row">
          <div className="md-prog-track" role="progressbar" aria-label="Progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={prog}>
            <div className="md-prog-fill" style={{width:`${prog}%`}}/>
          </div>
          <span className="md-prog-pct">{prog}%</span>
        </div>
        <div className="md-prog-lbl">{getProgressText(task)}</div>
      </div>
    );
  };

  const renderDue = (task) => {
    const dueBadge=getDueBadge(task,now);
    return (
      <>
        <span className={`md-due-badge ${dueBadge.cls}`}>{dueBadge.label}</span>
        {task.due_at && <div className="md-remark-meta">{formatShortDate(task.due_at)}</div>}
      </>
    );
  };

  // created_by_name comes from GET /tasks (users joined on t.created_by).
  // Never falls back to the raw id: a deleted/unknown creator shows "—".
  const renderCreatedBy = (task) => {
    const name = getText(task.created_by_name,"").trim()
      || (task.created_by && typeof task.created_by==="object" ? getText(task.created_by.name,"").trim() : "");
    const role=getText(task.created_by_role,"");
    return name
      ? <>
          <span className="mt-created-by" title={name}>{name}</span>
          {role && <span className="mt-person-role">{titleCase(role.replace(/[_-]+/g," "))}</span>}
        </>
      : <span className="mt-created-by none" aria-label="Creator not available">—</span>;
  };

  const renderOwner = (task) => {
    const curName=getText(task.assigned_to_name,"")||getText(task.assigned_to,"")||getText(task.assignedTo,"")||null;
    const role=curName?getText(task.assigned_to_role,""):"";
    // An IT Support complaint owned by a non-Technician (legacy data:
    // creation-time Manager fallback / older assignments) is shown as it
    // is, but marked -- it is not a valid NEW owner for a complaint.
    const nonStandard=!!role && isTeamComplaint(task) && normRole(role)!=="technician";
    return (
      <>
        <span className={`md-assignee-pill${curName?"":" none"}`}>{curName||"Not assigned"}</span>
        {role && <span className="mt-person-role">{titleCase(role.replace(/[_-]+/g," "))}</span>}
        {nonStandard && (
          <span className="mt-nonstandard-owner" title="IT Support complaints are owned by a Technician. Reassign this complaint to a Technician.">
            Non-standard owner
          </span>
        )}
      </>
    );
  };

  const renderAssign = (task) => {
    const draft=drafts[task.id]||{};
    const assignOptions=buildAssignOptions(task, teamMembers);
    const complaint=isTeamComplaint(task);
    // Noun follows the row's work group: Technician for complaints,
    // Developer / Data Analyst for that team's work.
    const noun=complaint ? "Technician" : (GROUP_ASSIGN_NOUN[workGroupOf(task)]||"");
    return (
      <select className="md-inline-select" aria-label={`Assign ${(noun||"technician").toLowerCase()} for ${getText(taskDisplayTitle(task))}`} value={draft.assigned_to||""} onChange={e=>onDraftChange(task.id,"assigned_to",e.target.value)}>
        <option value="">{noun ? `Select ${noun}` : "Select Tech"}</option>
        {assignOptions.map(t=>{
          const label=t.isCurrentOwnerOnly
            ? `${getText(t)} (current owner${getText(t.role_name,"")?` · ${getMemberRole(t)}`:""})`
            : `${getText(t)} (${getMemberRole(t)})`;
          return <option key={getIdValue(t)||getText(t)} value={getIdValue(t)}>{label}</option>;
        })}
      </select>
    );
  };

  // The ticket's SAVED category / subcategory (category_name /
  // sub_category_name from GET /tasks). Read-only: there is no category
  // update endpoint (the old inline dropdown's PUT /tasks/:id/category never
  // existed, so edits were silently lost), and a generic unit category list
  // cannot hold an Own Ticket's private category -- which is why the saved
  // value used to show as just "Category".
  // An id with no name = the category/subcategory row was deleted (no
  // foreign key on tasks); shown as such, never as an invented name.
  const savedLabel = (name, id, kind) => {
    const n=getText(name,"");
    if(n) return n;
    const i=getText(id,"");
    return i ? `Deleted ${kind} (#${i})` : "";
  };

  const renderCategory = (task) => {
    const cat=savedLabel(task.category_name, task.category_id, "category");
    const sub=savedLabel(task.sub_category_name, task.sub_category_id, "subcategory");
    if(!cat&&!sub) return <span className="md-no-remark">—</span>;
    return (
      <div className="mt-category">
        <span className="mt-category-name" title={cat}>{cat||"—"}</span>
        {sub && <span className="mt-subcategory-name" title={sub}>{sub}</span>}
      </div>
    );
  };

  const renderRemark = (task) => {
    const lr=getLastRemark(task);
    return lr ? (
      <div>
        <div className="md-remark-text" title={lr.text}>{lr.text}</div>
        <div className="md-remark-meta">{lr.type}{lr.by?` · ${lr.by}`:""}{lr.time?` · ${formatShortDate(lr.time)}`:""}</div>
      </div>
    ) : <span className="md-no-remark">No remark</span>;
  };

  const renderActions = (task) => (
    <div className="md-act-wrap">
      <button
        type="button"
        className="md-act-btn open"
        title="Open Ticket"
        aria-label={`Open ticket: ${getText(taskDisplayTitle(task))}`}
        onClick={() => onOpen(task)}
      >
        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8z" />
          <path d="M8 9h8" />
          <path d="M8 13h5" />
        </svg>
      </button>
      <button type="button" className="md-act-btn save" title="Save assignment and category" onClick={()=>onSave(task)} disabled={savingId===task.id} aria-busy={savingId===task.id||undefined}>
        {savingId===task.id?<Spin/>:"Save"}
      </button>
    </div>
  );

  const rowClass = (task) => isOverdueTask(task,now) ? "md-row-overdue" : normalizeStatus(task.status)==="pending" ? "md-row-pending" : "";

  const emptyState = (
    <div className="md-empty">
      <div className="md-empty-icon"><FaBoxOpen/></div>
      <h5>No tickets found</h5>
      <p>Try changing filters or create a new ticket.</p>
    </div>
  );

  return (
    <div className={`md-card md-page-section md-tasktable-full${standardized?" mtt-std":""}${showCreatedBy?" mtt-created-by":""}`} ref={tableRef}>
      <div className="md-toolbar">
        <div className="md-toolbar-left">
          <h5><FaListCheck className="md-anacard-icon" aria-hidden="true"/> {title}</h5>
          {subtitle && <p>{subtitle}</p>}
          {scopeNote && <span className="md-scope-note">{scopeNote}</span>}
        </div>
        <div className="md-toolbar-right">
          <div className="md-search-wrap">
            <span className="md-search-icon" aria-hidden="true">
              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </span>
            <input className="md-search" aria-label="Search tickets" placeholder="Search ticket, remark, category, technician…" value={search} onChange={e=>onSearchChange(e.target.value)} />
          </div>
          <select className="md-select md-select-status" aria-label="Status" value={status} onChange={e=>onStatusChange(e.target.value)}>
            <option value="ALL">All Status</option>
            {statusOptions.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className="md-select md-select-member" aria-label="Team member" value={member} onChange={e=>onMemberChange(e.target.value)}>
            <option value="ALL">All Users</option>
            {memberOptions.map(m=><option key={getIdValue(m)||getText(m)} value={getIdValue(m)}>{getText(m)}</option>)}
          </select>
          {toolbarExtra}
          <select className="md-select md-select-rows" aria-label="Rows per page" value={rowsPerPage} onChange={e=>onRowsPerPageChange(Number(e.target.value))}>
            {[5,10,20,50].map(n=><option key={n} value={n}>{n} rows</option>)}
          </select>
          {hasFilters && (
            <button type="button" className="md-btn md-btn-ghost md-btn-sm md-reset-btn" onClick={onReset}>Reset</button>
          )}
        </div>
      </div>

      <div className="md-chip-row" role="group" aria-label="Quick filters">
        <span className="md-chip-row-icon" aria-hidden="true"><FaFilter/></span>
        {QUICK_FILTER_CHIPS.map(([key,label])=>(
          <button key={key} type="button" className={`md-chip${quickFilter===key?" active":""}`} aria-pressed={quickFilter===key} onClick={()=>onQuickFilterChange(key)}>
            {label} <span className="md-chip-count">{chipCounts[key]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="md-table-wrap md-table-scroll">
          <div className="md-loader" role="status"><Spin lg/><p>Loading tickets…</p></div>
        </div>
      ) : showCards ? (
        pageData.length===0 ? emptyState : (
          <ul className="md-ccards" aria-label={title}>
            {pageData.map((task,idx)=>(
              <li key={task.id} className={`md-ccard ${rowClass(task)}`}>
                <div className="md-ccard-head">
                  <span className="md-ccard-no">#{i0+idx+1}</span>
                  {renderTask(task)}
                </div>
                <dl className="md-ccard-fields">
                  {showCreatedBy && <div><dt>Created By</dt><dd>{renderCreatedBy(task)}</dd></div>}
                  <div><dt>Priority</dt><dd>{renderPriority(task)}</dd></div>
                  <div><dt>Status &amp; Progress</dt><dd>{renderProgress(task)}</dd></div>
                  <div><dt>Due / SLA</dt><dd>{renderDue(task)}</dd></div>
                  <div><dt>Current Owner</dt><dd>{renderOwner(task)}</dd></div>
                  <div><dt>{assignLabel}</dt><dd>{renderAssign(task)}</dd></div>
                  <div><dt>Category</dt><dd>{renderCategory(task)}</dd></div>
                  <div><dt>Last Remark</dt><dd>{renderRemark(task)}</dd></div>
                </dl>
                <div className="md-ccard-actions">{renderActions(task)}</div>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="md-table-wrap md-table-scroll" role="region" aria-label={`${title} table`} tabIndex={0}>
          <table className="md-table md-table-sticky">
            <thead>
              <tr>
                <th scope="col" className="mt-col-no" style={{width:52}}>#</th>
                <th scope="col" className="mt-col-task">Ticket</th>
                {showCreatedBy && <th scope="col" className="mt-col-created-by">Created By</th>}
                <th scope="col" className="mt-col-priority" style={{width:90}}>Priority</th>
                <th scope="col" className="mt-col-status" style={{width:170}}>Status &amp; Progress</th>
                <th scope="col" className="mt-col-due" style={{width:100}}>Due / SLA</th>
                <th scope="col" className="mt-col-owner">Current Owner</th>
                <th scope="col" className="mt-col-assign"><FaUserGear aria-hidden="true"/> {assignLabel}</th>
                <th scope="col" className="mt-col-category">Category</th>
                <th scope="col" className="mt-col-remark" style={{width:190}}>Last Remark</th>
                <th scope="col" className="mt-col-action" style={{width:130,textAlign:"right"}}>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length===0 ? (
                <tr><td colSpan={showCreatedBy ? 11 : 10}>{emptyState}</td></tr>
              ) : pageData.map((task,idx)=>(
                <tr key={task.id} className={rowClass(task)}>
                  <td className="mt-col-no" style={{fontFamily:"monospace",fontSize:11,color:"var(--md-text-meta, #C4BEB8)"}}>#{i0+idx+1}</td>
                  <td className="mt-col-task">{renderTask(task)}</td>
                  {showCreatedBy && <td className="mt-col-created-by">{renderCreatedBy(task)}</td>}
                  <td className="mt-col-priority">{renderPriority(task)}</td>
                  <td className="mt-col-status">{renderProgress(task)}</td>
                  <td className="mt-col-due">{renderDue(task)}</td>
                  <td className="mt-col-owner">{renderOwner(task)}</td>
                  <td className="mt-col-assign">{renderAssign(task)}</td>
                  <td className="mt-col-category">{renderCategory(task)}</td>
                  <td className="mt-col-remark">{renderRemark(task)}</td>
                  <td className="mt-col-action">{renderActions(task)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="md-footer">
        <span>Showing <strong>{filtered.length===0?0:i0+1}</strong>–<strong>{Math.min(i0+rowsPerPage,filtered.length)}</strong> of <strong>{filtered.length}</strong></span>
        {totalPages>1 && (
          <div className="md-page-btns">
            <button type="button" className="md-page-btn" aria-label="Previous page" disabled={page===1} onClick={()=>onPageChange(page-1)}>‹</button>
            {compactPageItems([...Array(totalPages)].map((_,i)=>(
              <button type="button" key={i} className={`md-page-btn${i+1===page?" active":""}`} aria-label={`Page ${i+1}`} aria-current={i+1===page?"page":undefined} onClick={()=>onPageChange(i+1)}>{i+1}</button>
            )), page, { renderEllipsis: (key) => <span key={key} className="md-page-btn md-page-gap" aria-hidden="true">…</span> })}
            <button type="button" className="md-page-btn" aria-label="Next page" disabled={page===totalPages} onClick={()=>onPageChange(page+1)}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}
